import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { EditIcon } from "lucide-react";

/**
 * Modal de edição avançada para Admin.
 * Permite corrigir n° de série, datas técnicas, lote, cliente, tara, etc.
 */
export default function ContainerEditModal({ open, container, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (container) {
      setForm({
        no_serie: container.no_serie || "",
        tipo_armazenamento: container.tipo_armazenamento || "TANQUE",
        capacidade: container.capacidade || 5000,
        tara: container.tara || 0,
        data_teste_carga: container.data_teste_carga || "",
        data_hidrostatico: container.data_hidrostatico || "",
        data_estanqueidade: container.data_estanqueidade || "",
        local_estoque: container.local_estoque || "",
        nome_produto: container.nome_produto || "",
        no_lote: container.no_lote || "",
        cliente: container.cliente || "",
        qtd_envasado: container.qtd_envasado || 0,
        data_validade_lote: container.data_validade_lote || "",
      });
    }
  }, [container]);

  const submit = async () => {
    if (!container) return;
    setBusy(true);
    try {
      const payload = {};
      Object.keys(form).forEach((k) => {
        const v = form[k];
        if (v === "" || v === null || v === undefined) return;
        // Só envia campos que mudaram
        if (v !== container[k]) payload[k] = v;
      });
      if (!Object.keys(payload).length) { toast.info("Nenhuma alteração para salvar"); setBusy(false); return; }
      // Normalize types
      if (payload.capacidade) payload.capacidade = parseInt(payload.capacidade, 10);
      if (payload.tara !== undefined) payload.tara = parseFloat(payload.tara);
      if (payload.qtd_envasado !== undefined) payload.qtd_envasado = parseFloat(payload.qtd_envasado);
      const res = await api.patch(`/containers/${container.no_serie}`, payload);
      toast.success(`Contentor ${res.data.no_serie} atualizado`);
      onSaved?.(res.data.no_serie);
      onClose?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!container) return null;
  const F = (name, label, opts = {}) => (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <Input
        type={opts.type || "text"}
        step={opts.step}
        value={form[name] ?? ""}
        onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        data-testid={`edit-${name}`}
        className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-9"
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-[#14151A] border-border rounded-none max-w-3xl" data-testid="container-edit-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <EditIcon className="w-5 h-5 text-[#FFD60A]" /> Edição Avançada · <span className="font-mono text-[#FFD60A]">{container.no_serie}</span>
          </DialogTitle>
          <div className="text-xs text-muted-foreground">
            Correção de dados operacionais. Ao alterar o <b>N° Série</b>, o histórico completo do contentor é migrado.
          </div>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {F("no_serie", "N° Série *")}
          {F("tipo_armazenamento", "Tipo")}
          {F("capacidade", "Capacidade (L)", { type: "number" })}
          {F("tara", "Tara (kg)", { type: "number", step: "0.01" })}
          {F("local_estoque", "Local de Estoque")}
          {F("nome_produto", "Produto")}
          {F("no_lote", "N° Lote")}
          {F("qtd_envasado", "Qtd Envasado (L)", { type: "number" })}
          {F("cliente", "Cliente Destino")}
          <div className="md:col-span-3 text-[10px] uppercase tracking-widest text-[#00E5FF] mt-2 pb-1 border-b border-border/50">Datas Técnicas</div>
          {F("data_teste_carga", "Teste de Carga", { type: "date" })}
          {F("data_hidrostatico", "Teste Hidrostático", { type: "date" })}
          {F("data_estanqueidade", "Teste de Estanqueidade", { type: "date" })}
          {F("data_validade_lote", "Validade do Lote", { type: "date" })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-border">Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="edit-submit" className="rounded-none bg-[#FFD60A] text-black hover:bg-[#FFD60A]/85 font-bold uppercase tracking-wider">
            {busy ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
